<script setup>
import {ref, computed, watch, nextTick, onBeforeUnmount} from 'vue'
import {Tooltip} from 'bootstrap'
import {useItemDataStore} from '@/stores/itemData'

const props = defineProps({
    userId: Number,
    tooltipPlacement: String,
    isSmall: Boolean
})

const itemDataStore = useItemDataStore()

// --- State ---

const userData = ref(null)
const tooltipRef = ref(null)
let tooltipInstance = null

// --- Computed ---

const imgSrc = computed(() => {
    if (userData.value?.gravatarImageSrc) {
        return `${userData.value.gravatarImageSrc}?s=32`
    }
    return null
})

const initials = computed(() => {
    const first = userData.value?.firstName?.charAt(0) || ''
    const last = userData.value?.lastName?.charAt(0) || ''
    return first + last
})

// --- Methods ---

const destroyTooltip = () => {
    if (tooltipInstance) {
        tooltipInstance.dispose()
        tooltipInstance = null
    }
}

const initTooltip = () => {
    destroyTooltip()

    // Only present a tooltip if its placement has been set explicitly.
    if (props.tooltipPlacement && tooltipRef.value) {
        tooltipInstance = new Tooltip(tooltipRef.value)
    }
}

const fetchUser = async (id) => {
    if (!id) {
        userData.value = null
        return
    }

    userData.value = await itemDataStore.get({
        tableName: 'users',
        id: id
    })
}

// --- Lifecycle & Watchers ---

// Re-fetch user when userId changes
watch(() => props.userId, (newId) => {
    fetchUser(newId)
}, {immediate: true})

// Manage Tooltip lifecycle when userData or placement changes
watch([userData, () => props.tooltipPlacement], () => {
    nextTick(() => {
        initTooltip()
    })
})

onBeforeUnmount(() => {
    destroyTooltip()
})
</script>

<template>
    <span
        v-if="userData"
        ref="tooltipRef"
        class="user-avatar"
        :class="{ 'user-avatar-with-text': initials, 'user-avatar-sm': isSmall }"
        :data-bs-placement="tooltipPlacement"
        :title="userData.displayName"
        data-bs-toggle="tooltip"
    >
        <img v-if="imgSrc" :src="imgSrc" alt=""/>
        <span v-if="initials">{{ initials }}</span>
    </span>
</template>

<style scoped>
.user-avatar {
    display: inline-block;
    width: 25px;
    height: 25px;
    border-radius: 50%;
    line-height: 100%;
    vertical-align: middle;
    overflow: hidden;
}

.user-avatar-with-text {
    font-size: 12px;
    line-height: 25px;
    text-align: center;
    text-transform: uppercase;
    color: #3373f3;
    background: #ccc;
    font-weight: 500;
}

.user-avatar > img {
    width: 100%;
    height: 100%;
}

.user-avatar-sm {
    margin-top: 3px;
    width: 21px;
    height: 21px;
}

.user-avatar-sm.user-avatar-with-text {
    padding-left: 1px;
    line-height: 22px;
    font-size: 10px;
    font-weight: 600;
}
</style>